import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";

const FAQS = [
  {
    q: "How long does FlowSync take to deploy?",
    a: "Most teams go live in under 30 days. Our onboarding team helps with setup, data migration, user management, and workflow configuration.",
  },
  {
    q: "Can we integrate with our existing systems?",
    a: "Yes. FlowSync supports integrations with ERP, CRM, accounting software, inventory management tools, and custom APIs.",
  },
  {
    q: "Does FlowSync support role-based access control?",
    a: "Absolutely. Administrators can assign roles, manage permissions, and control access to specific business modules.",
  },
  {
    q: "How does revenue tracking work?",
    a: "FlowSync allows you to track revenue, categorize income, add financial notes, and generate date-wise reports.",
  },
  {
    q: "Can I monitor manufacturing progress in real time?",
    a: "Yes. Our manufacturing module provides live production tracking, inventory allocation, production planning, and status monitoring.",
  },
  {
    q: "What support do you provide?",
    a: "We offer email support for all customers, priority support for premium plans, and dedicated onboarding assistance for enterprise clients.",
  },
];

export default function FAQ() {
  const [expanded, setExpanded] = useState(-1);

  const handleChange = (index) => {
    setExpanded(expanded === index ? -1 : index);
  };

  return (
    <Box
      component="section"
      id="faq"
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 10, md: 14 },

        background:
          "linear-gradient(180deg, #f8fafc 0%, #eef6ff 50%, #f8fafc 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -200,
          left: -200,
          width: 600,
          height: 600,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.22), transparent 60%)",
          filter: "blur(100px)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: -180,
          right: -220,
          width: 600,
          height: 600,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(14,165,233,0.18), transparent 60%)",
          filter: "blur(110px)",
        }}
      />

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: 6 }}>
          <Typography
            sx={{
              fontWeight: 800,
              letterSpacing: 1.2,
              color: "#2563eb",
              textTransform: "uppercase",
              fontSize: "0.8rem",
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: "2rem", md: "2.6rem" },
              lineHeight: 1.2,
              color: "#0f172a",
            }}
          >
            Got Questions?
            <Box
              component="span"
              sx={{ display: "block", color: "#475569", fontWeight: 700 }}
            >
              We've Got Answers.
            </Box>
          </Typography>

          <Typography
            sx={{
              maxWidth: 700,
              lineHeight: 1.8,
              color: "#475569",
              fontSize: "1rem",
            }}
          >
            Learn how FlowSync helps businesses manage products, manufacturing,
            revenue, analytics, and operations through one powerful platform.
          </Typography>
        </Stack>

        <Stack spacing={2}>
          {FAQS.map((faq, index) => {
            const isExpanded = expanded === index;

            return (
              <Accordion
                key={index}
                expanded={isExpanded}
                onChange={() => handleChange(index)}
                disableGutters
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",

                  background: "#ffffff",
                  border: "1px solid rgba(226,232,240,0.9)",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.05)",

                  transition: "all 0.3s ease",

                  "&:before": { display: "none" },

                  "&:hover": {
                    borderColor: "rgba(37,99,235,0.3)",
                    boxShadow: "0 18px 50px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    isExpanded ? (
                      <RemoveRoundedIcon sx={{ color: "#2563eb" }} />
                    ) : (
                      <AddRoundedIcon sx={{ color: "#2563eb" }} />
                    )
                  }
                  sx={{
                    px: 3,
                    py: 1.5,

                    "& .MuiAccordionSummary-content": {
                      margin: "14px 0",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "1rem", md: "1.05rem" },
                      color: "#0f172a",
                    }}
                  >
                    {faq.q}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 3, pb: 3 }}>
                  <Typography
                    sx={{
                      lineHeight: 1.8,
                      color: "#475569",
                    }}
                  >
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      </Container>
    </Box>
  );
}